import { PlatformError, validateCuratedSfx, audioSelections, type DocStorePort, type DocTxRunner, type DocTxOps } from '@djgg/core';
import { validateAssetRejectionV1, type AssetRejectionV1 } from '../mediaContract.js';
import { assetRejectionDoc, assetRejectionHistoryDoc, assetVariantDoc, assetVariantsCollection, creationDoc } from '../paths.js';
import type { Creation, AssetVariantDoc } from '../types.js';

export interface ActiveAssetRejectionContext {
  /**
   * Advisory generation context only. Rejection enforcement remains the
   * exact-byte checks at attachment and publication time.
   */
  entries: readonly Readonly<{
    contentSha256: string;
    name?: string;
    variantId?: string;
    url?: string;
  }>[];
  /** True when this bounded advisory view could not represent every row. */
  truncated: boolean;
}

const MAX_CONTEXT_ENTRIES = 64;
const MAX_REJECTION_ROWS = 1000;

function rejectionPath(creationId: string, digest: string): string {
  if (!/^[A-Za-z0-9][A-Za-z0-9_-]*$/.test(creationId) || !/^[0-9a-f]{64}$/.test(digest)) {
    throw new PlatformError('validation', 'invalid media rejection identity');
  }
  return assetRejectionDoc(creationId, digest);
}

/** Server-only prerequisite. No public Reject endpoint until all publication,
 * generation and bulk-attachment paths enforce the policy. Callers own auth
 * and must resolve the digest from trusted stored bytes, never a client URL. */
export class AssetRejectionsRepo {
  constructor(private readonly store: DocStorePort) {}

  /**
   * Read a bounded, server-derived description of active rejection decisions
   * for generation context. Names identify a past candidate only: callers
   * must not treat them as a ban on a replacement with the same name. URLs
   * are included solely when the stored candidate still proves the exact
   * rejected byte identity.
   *
   * This deliberately reports an incomplete/corrupt view as `truncated`
   * rather than presenting it as a complete policy. It is advisory; the
   * policy checks below remain authoritative.
   */
  async listActiveContext(creationId: string): Promise<ActiveAssetRejectionContext> {
    if (!/^[A-Za-z0-9][A-Za-z0-9_-]*$/.test(creationId)) {
      throw new PlatformError('validation', 'invalid media rejection creation identity');
    }
    const rows = await this.store.query<{ record: unknown }>(
      `${creationDoc(creationId)}/assetRejections`,
      { limit: MAX_REJECTION_ROWS + 1 },
    );
    let truncated = rows.length > MAX_REJECTION_ROWS;
    const records = new Map<string, AssetRejectionV1>();
    for (const row of rows.slice(0, MAX_REJECTION_ROWS)) {
      let record: AssetRejectionV1;
      try {
        record = validateAssetRejectionV1(row.record);
      } catch {
        truncated = true;
        continue;
      }
      if (record.creationId !== creationId || record.contentSha256 !== row.id) {
        truncated = true;
        continue;
      }
      if (record.undo) continue;
      if (records.has(record.contentSha256)) {
        truncated = true;
        continue;
      }
      records.set(record.contentSha256, record);
    }
    const selected = [...records.values()].sort((a, b) =>
      a.contentSha256.localeCompare(b.contentSha256) ||
      (a.variantId ?? '').localeCompare(b.variantId ?? '') ||
      (a.name ?? '').localeCompare(b.name ?? ''),
    );
    if (selected.length > MAX_CONTEXT_ENTRIES) truncated = true;
    const bounded = selected.slice(0, MAX_CONTEXT_ENTRIES);
    // One query for every referenced variant (staged ones included, exactly as
    // a direct doc read sees them): a per-record fan-out would queue one pool
    // acquisition per entry behind the small per-process pool.
    const referenced = [...new Set(bounded.flatMap(record => record.variantId ? [record.variantId] : []))];
    const candidates = referenced.length === 0 ? new Map<string, AssetVariantDoc>() : new Map(
      (await this.store.query<AssetVariantDoc>(assetVariantsCollection(creationId), {
        where: [['variantId', 'in', referenced]],
      })).map(row => [row.variantId, row]),
    );
    const resolved = bounded.map(record => {
      let url: string | undefined;
      let incomplete = false;
      if (record.variantId) {
        const candidate = candidates.get(record.variantId);
        if (candidate?.creationId === creationId && candidate.variantId === record.variantId &&
          candidate.contentSha256 === record.contentSha256) {
          if (typeof candidate.url === 'string') url = candidate.url;
          else incomplete = true;
        }
      }
      return {
        entry: Object.freeze({
          contentSha256: record.contentSha256,
          ...(record.name ? { name: record.name } : {}),
          ...(record.variantId ? { variantId: record.variantId } : {}),
          ...(url ? { url } : {}),
        }),
        incomplete,
      };
    });
    if (resolved.some(item => item.incomplete)) truncated = true;
    return Object.freeze({ entries: Object.freeze(resolved.map(item => item.entry)), truncated });
  }

  /** Explicit server-owned undo. Preserve the original audit, reopen rejected
   * same-byte candidates, and never reattach media or mutate published history.
   * Authentication/attribution must be resolved by the future caller endpoint. */
  async undo(txRunner: DocTxRunner, input: {
    creationId: string; contentSha256: string; actorUid: string; atMs: number; rejectedAtMs: number;
  }): Promise<{ undone: boolean }> {
    const path = rejectionPath(input.creationId, input.contentSha256);
    return txRunner.runTransaction(async tx => {
      const creation = await tx.get<Creation>(creationDoc(input.creationId));
      if (!creation) throw new PlatformError('not-found', 'creation not found');
      const prior = await tx.get<{ record: AssetRejectionV1 }>(path);
      if (!prior) throw new PlatformError('not-found', 'media rejection not found');
      const original = validateAssetRejectionV1(prior.record);
      if (original.rejectedAtMs !== input.rejectedAtMs) {
        throw new PlatformError('conflict', 'undo targets a stale rejection; refresh the current decision');
      }
      if (original.creationId !== input.creationId || original.contentSha256 !== input.contentSha256) {
        throw new PlatformError('conflict', 'media rejection record identity mismatch');
      }
      const record = validateAssetRejectionV1({ ...original, undo: { actorUid: input.actorUid, atMs: input.atMs } });
      if (original.undo) return { undone: false };
      const revision = creation.assetPolicyRevision ?? 0;
      if (!Number.isSafeInteger(revision) || revision < 0 || revision >= Number.MAX_SAFE_INTEGER) {
        throw new PlatformError('conflict', 'invalid asset policy revision');
      }
      if (original.variantId) {
        const candidatePath = assetVariantDoc(input.creationId, original.variantId);
        const candidate = await tx.get<AssetVariantDoc>(candidatePath);
        if (candidate && candidate.contentSha256 !== input.contentSha256) {
          throw new PlatformError('conflict', 'undo candidate does not match trusted byte identity');
        }
      }
      for (const candidate of await this.matchingCandidates(tx, input.creationId, input.contentSha256)) {
        if (candidate.reviewState === 'rejected') await tx.update(assetVariantDoc(input.creationId, candidate.variantId), {
          reviewState: 'pending', reviewedAt: this.store.serverTimestamp(),
        });
      }
      await tx.set(path, { record });
      await this.recordTransition(tx, revision + 1, 'undo', record, original);
      await tx.update(creationDoc(input.creationId), { assetPolicyRevision: revision + 1 });
      return { undone: true };
    });
  }

  private async matchingCandidates(tx: DocTxOps, creationId: string, digest: string): Promise<AssetVariantDoc[]> {
    if (!tx.query) throw new PlatformError('unavailable', 'media policy requires transactional candidate discovery');
    // Every append/reject/undo holds creation first, so candidates cannot be
    // inserted/renamed under this discovery. Do not use a second pool query.
    const candidates = await tx.query<AssetVariantDoc>(assetVariantsCollection(creationId), {
      where: [['contentSha256', '==', digest]], orderBy: [['variantId', 'asc']], limit: 1001,
    });
    if (candidates.length > 1000) throw new PlatformError('conflict', 'too many matching candidates for an atomic media decision');
    return candidates;
  }

  private async recordTransition(tx: DocTxOps, revision: number, operation: 'reject' | 'undo',
    record: AssetRejectionV1, priorRecord?: AssetRejectionV1): Promise<void> {
    const path = assetRejectionHistoryDoc(record.creationId, revision);
    if (await tx.get(path)) throw new PlatformError('conflict', 'media audit revision already exists');
    await tx.set(path, { operation, record, ...(priorRecord ? { priorRecord } : {}) });
  }

  async assertMaterializedAllowed(creationId: string, inventory: unknown, revision = 0, tx?: DocTxOps): Promise<void> {
    if (inventory === undefined && revision === 0) return;
    const value = inventory as { schemaVersion?: unknown; complete?: unknown; digests?: unknown } | undefined;
    if (!value || value.schemaVersion !== 1 || typeof value.complete !== 'boolean' ||
      !Array.isArray(value.digests) || value.digests.length > 1000 ||
      !value.digests.every(d => typeof d === 'string' && /^[0-9a-f]{64}$/.test(d))) {
      throw new PlatformError('conflict', 'new version needs a verified materialized-media inventory');
    }
    if (revision > 0 && !value.complete) throw new PlatformError('conflict', 'unresolved external media cannot publish under a rejection policy');
    for (const digest of value.digests) await this.assertAllowed(creationId, digest, revision, tx);
    const sfx = (inventory as { sfx?: unknown }).sfx;
    if (sfx !== undefined) for (const binding of audioSelections(validateCuratedSfx(sfx))) {
      if (binding !== 'none' && !value.digests.includes(binding.contentSha256)) throw new PlatformError('conflict', 'SFX missing from media inventory');
    }
  }

  /** New publication must use the current trusted selection, not model output
   * or a stale pre-edit draft. Historical playback itself never calls this. */
  async assertSfxCurrent(creationId: string, inventory: unknown, tx?: DocTxOps): Promise<void> {
    const creation = tx ? await tx.get<Creation>(creationDoc(creationId)) : await this.store.getDoc<Creation>(creationDoc(creationId));
    const expected = creation?.sfxDraft?.spec;
    const actual = (inventory as { sfx?: unknown } | undefined)?.sfx;
    if (JSON.stringify(expected === undefined ? null : validateCuratedSfx(expected)) !==
      JSON.stringify(actual === undefined ? null : validateCuratedSfx(actual))) throw new PlatformError('conflict', 'Sound selection changed before publication; rebuild the current draft');
  }

  /** Validate a new version's recorded asset inventory. This is not artifact
   * closure proof: callers must separately prevent undeclared embedded URLs. */
  async assertInventoryAllowed(creationId: string, assets: readonly { contentSha256?: string }[] | undefined,
    revision = 0, tx?: DocTxOps): Promise<void> {
    if (revision > 0 && assets === undefined) {
      throw new PlatformError('conflict', 'new version needs an asset inventory under a rejection policy');
    }
    for (const asset of assets ?? []) await this.assertAllowed(creationId, asset.contentSha256, revision, tx);
  }

  /** For callers already holding the creation lock, use tx reads rather than
   * opening a second connection/transaction behind that lock. */
  async assertAllowed(creationId: string, digest: string | undefined, revision = 0, tx?: DocTxOps): Promise<void> {
    if (!digest) {
      if (revision > 0) throw new PlatformError('conflict', 'asset identity must be resolved before attaching under a rejection policy');
      return; // legacy creation without any recorded policy
    }
    const path = rejectionPath(creationId, digest);
    const row = tx ? await tx.get<{ record: AssetRejectionV1 }>(path)
      : await this.store.getDoc<{ record: AssetRejectionV1 }>(path);
    if (row) {
      const record = validateAssetRejectionV1(row.record);
      if (record.creationId !== creationId || record.contentSha256 !== digest) {
        throw new PlatformError('conflict', 'media rejection record identity mismatch');
      }
      if (!record.undo) throw new PlatformError('conflict', 'these exact media bytes were rejected for this creation');
    }
  }

  /** Atomic policy + working-head detach. The revision fences attachAsset's
   * CAS: a rejection racing that attachment either invalidates its CAS or
   * detaches it afterwards. Published versions/cover pointers are untouched.
   * In-memory tests are not proof of Postgres lock behavior. */
  async reject(txRunner: DocTxRunner, input: AssetRejectionV1): Promise<{ recorded: boolean; detached: number }> {
    const record = validateAssetRejectionV1(input);
    if (record.undo) throw new PlatformError('validation', 'reject cannot supply undo metadata');
    const path = rejectionPath(record.creationId, record.contentSha256);
    return txRunner.runTransaction(async tx => {
      const creation = await tx.get<Creation>(creationDoc(record.creationId));
      if (!creation) throw new PlatformError('not-found', 'creation not found');
      const candidate = record.variantId ? await tx.get<AssetVariantDoc>(assetVariantDoc(record.creationId, record.variantId)) : null;
      if (record.variantId && (!candidate || candidate.contentSha256 !== record.contentSha256)) {
        throw new PlatformError('conflict', 'rejection candidate does not match trusted byte identity');
      }
      const prior = await tx.get<{ record: AssetRejectionV1 }>(path);
      if (prior) {
        const existing = validateAssetRejectionV1(prior.record);
        if (existing.creationId !== record.creationId || existing.contentSha256 !== record.contentSha256) {
          throw new PlatformError('conflict', 'media rejection record identity mismatch');
        }
        if (!existing.undo) return { recorded: false, detached: 0 };
        if (record.rejectedAtMs <= existing.undo.atMs) {
          throw new PlatformError('conflict', 'stale rejection cannot replace a later undo');
        }
      }
      const revision = creation.assetPolicyRevision ?? 0;
      if (!Number.isSafeInteger(revision) || revision < 0 || revision >= Number.MAX_SAFE_INTEGER) {
        throw new PlatformError('conflict', 'invalid asset policy revision');
      }
      const assets = creation.assets ?? [];
      const retained = assets.filter(a => a.contentSha256 !== record.contentSha256);
      const matching = await this.matchingCandidates(tx, record.creationId, record.contentSha256);
      await tx.set(path, { record });
      await this.recordTransition(tx, revision + 1, 'reject', record, prior?.record);
      for (const match of matching) await tx.update(assetVariantDoc(record.creationId, match.variantId), {
        reviewState: 'rejected', reviewedAt: this.store.serverTimestamp(),
      });
      await tx.update(creationDoc(record.creationId), {
        assets: retained, assetPolicyRevision: revision + 1,
        ...(creation.sfxDraft ? { sfxDraft: { ...creation.sfxDraft, actorUid: record.actorUid, updatedAtMs: record.rejectedAtMs,
          spec: { ...creation.sfxDraft.spec,
            ...(creation.sfxDraft.spec.music !== undefined ? { music: creation.sfxDraft.spec.music !== 'none' && creation.sfxDraft.spec.music.contentSha256 === record.contentSha256 ? 'none' : creation.sfxDraft.spec.music } : {}),
            cues: Object.fromEntries(Object.entries(creation.sfxDraft.spec.cues).map(([cue, binding]) =>
            [cue, binding !== 'none' && binding.contentSha256 === record.contentSha256 ? 'none' : binding])) },
        } } : {}),
      });
      return { recorded: true, detached: assets.length - retained.length };
    });
  }
}
